// lib/fbp.js
// El browser id de Meta (_fbp): identifica al navegador entre eventos, y es el
// parámetro de match más barato que existe porque no depende de que el usuario deje
// ningún dato.
//
// El único que escribe la cookie es fbevents.js. Si el pixel está bloqueado, o si
// todavía no terminó de bajar cuando sale el primer evento, el _fbp no existe y el
// CAPI sale sin él: la cobertura en fb_ads_params venía de 88,6% en ene-2026 a
// 65-72% de abr-2026 en adelante, o sea un tercio de los checkouts sin browser id.
// Es lo que Events Manager marca como "low coverage of fbp through Conversions API".
//
// El _fbp no es un dato de Meta que haya que esperar: es `fb.{subdominio}.{ts}.{rand}`,
// un id que se puede generar de este lado. Eso hace acá ensureFbp(), y como fbevents.js
// sólo escribe la cookie si no existe, cuando el pixel carga adopta la nuestra: el hit
// de navegador y el del CAPI siguen mandando el MISMO id (si mandaran ids distintos
// Meta vería dos navegadores y el match empeoraría en vez de mejorar).
//
// Mismo criterio que lib/fbclid.js para el _fbc: cookie propia a 90 días más espejo en
// localStorage, que acá es lo que evita que ITP (recorta a 7 días lo que escribe JS)
// nos haga rotar el id cada semana, que sería peor que no tenerlo.

import { getCookie } from "@/utils/cookies";
import { subdominioIndex } from "@/lib/fbclid";

const FBP_COOKIE = "_fbp";
const ESPEJO_KEY = "bcm_fbp";
const MAX_AGE_DAYS = 90;

// fb.<subdominio>.<ts en ms>.<random>, tal cual lo arma fbevents.js.
const FORMATO = /^fb\.\d+\.\d{10,}\.\d+$/;

/** Entero grande al azar para la última parte del _fbp (10 dígitos como el del pixel). */
function randomId() {
  try {
    const buf = new Uint32Array(1);
    window.crypto.getRandomValues(buf);
    if (buf[0] > 0) return buf[0];
  } catch {
    /* sin webcrypto (http, browsers viejos) */
  }
  return Math.floor(Math.random() * 4294967295) + 1;
}

function leerEspejo() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(ESPEJO_KEY);
    if (!raw) return null;
    const { v, t } = JSON.parse(raw);
    if (v && t && Date.now() - t < MAX_AGE_DAYS * 864e5 && FORMATO.test(v)) return v;
  } catch {
    /* localStorage bloqueado (modo privado) */
  }
  return null;
}

function persistir(valor) {
  const maxAge = MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${FBP_COOKIE}=${valor}; max-age=${maxAge}; path=/; SameSite=Lax`;
  try {
    window.localStorage.setItem(ESPEJO_KEY, JSON.stringify({ v: valor, t: Date.now() }));
  } catch {
    /* ignore */
  }
}

/**
 * El _fbp vigente, sin efectos: cookie -> espejo. null si no hay nada todavía.
 */
export function getFbp() {
  const cookie = getCookie(FBP_COOKIE);
  if (cookie && FORMATO.test(cookie)) return cookie;
  return leerEspejo();
}

/**
 * El _fbp vigente, creándolo si hace falta. Es el que usan los call sites (CAPI y
 * checkout) y se llama además una vez por pageload (GoogleAdsTag) para que la cookie
 * ya exista cuando salga el primer evento.
 *
 * Prioridad: cookie del pixel -> espejo en localStorage (se re-escribe la cookie con
 * el MISMO valor, que es lo que salva el id de ITP) -> uno nuevo.
 */
export function ensureFbp() {
  if (typeof window === "undefined" || typeof document === "undefined") return null;
  try {
    const cookie = getCookie(FBP_COOKIE);
    if (cookie && FORMATO.test(cookie)) {
      // El espejo sigue SIEMPRE al valor vivo. Si se quedara con uno anterior (el que
      // generamos nosotros en una visita con el pixel bloqueado, pongamos), el día que
      // ITP borre la cookie resucitaríamos un id muerto en vez del que Meta ya conoce.
      if (leerEspejo() !== cookie) {
        try {
          window.localStorage.setItem(ESPEJO_KEY, JSON.stringify({ v: cookie, t: Date.now() }));
        } catch {
          /* ignore */
        }
      }
      return cookie;
    }

    const delEspejo = leerEspejo();
    const valor = delEspejo || `fb.${subdominioIndex()}.${Date.now()}.${randomId()}`;
    persistir(valor);
    return valor;
  } catch {
    /* nunca romper la página por analytics */
    return null;
  }
}
